import * as THREE from 'three';
import {RationalBezierCurveEvaluator2D} from '../../math/RationalBezierCurveEvaluator2D';
import {RationalBSplineCurveEvaluator2D} from '../../math/RationalBSplineCurveEvaluator2D';
import {
    SketchBezierCurveClosureKind,
    SketchBezierCurveKind,
} from '../../mesh/sketch/SketchSplineObject';
import type {SketchSplineObject} from '../../mesh/sketch/SketchSplineObject';
import type {SketchStyleSplineObject} from '../../mesh/sketch/SketchStyleSplineObject';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../config/SketchGeometryToleranceConfig';
import {buildClampedUniformKnots} from '../../tool/curveSamplingMath';
import {
    resolvePeriodicSplineControlPoints,
    resolveUniqueClosedSplineInterpolationPoints,
} from '../../tool/sampleClosedSplineInterpolationPoints';

/**
 * Trim 样条数学定义解析器。
 *
 * 职责：
 * 1. 把普通样条和样式样条转换为 `math` 层可消费的真实 Bezier/B-Spline 定义；
 * 2. 统一处理开放、闭合、周期曲线的原生参数与 Trim `[0,1]` 参数映射；
 * 3. 使用同一数学定义计算裁剪交点对应的真实世界坐标。
 *
 * 本类不会读取显示折线来拟合曲线。开放插值样条的权威定义尚未由 Worker 返回时，除两点退化线性
 * 情形外返回 `null`，由调用方决定暂缓精确裁剪还是采用既有兼容路径。
 */
export class SketchTrimSplineIntersectionDefinitionResolver {
    /** 二维有理 Bezier 求值器。 */
    private readonly bezierEvaluator: RationalBezierCurveEvaluator2D;

    /** 二维有理 B-Spline 求值器。 */
    private readonly bsplineEvaluator: RationalBSplineCurveEvaluator2D;

    /**
     * 创建 Trim 样条数学定义解析器。
     * @param bezierEvaluator 二维有理 Bezier 求值器。
     * @param bsplineEvaluator 二维有理 B-Spline 求值器。
     */
    public constructor(
        bezierEvaluator: RationalBezierCurveEvaluator2D = new RationalBezierCurveEvaluator2D(),
        bsplineEvaluator: RationalBSplineCurveEvaluator2D = new RationalBSplineCurveEvaluator2D(),
    ) {
        this.bezierEvaluator = bezierEvaluator;
        this.bsplineEvaluator = bsplineEvaluator;
    }

    /**
     * 解析草图样条的真实二维参数曲线定义。
     *
     * @param spline 普通样条或样式样条。
     * @returns `math` 层曲线定义；权威定义暂不可用时返回 `null`。
     */
    public resolveDefinition(
        spline: SketchSplineObject | SketchStyleSplineObject,
    ): LineSplineCurveDefinition2D | null {
        const metadata = spline.getCurveMetadata();
        const controlPoints = spline.getControlPoints().map((point) => (
            spline.referencePlane.worldToLocal(point.getPosition())
        ));
        if (controlPoints.length < 2) {
            return null;
        }

        if (metadata.curveKind === SketchBezierCurveKind.Bezier) {
            const closedControlPoints = this.closeControlPointsIfNeeded(
                controlPoints,
                metadata.closureKind === SketchBezierCurveClosureKind.Closed,
            );
            return {
                kind: 'bezier',
                controlPoints: closedControlPoints,
                weights: this.resolveWeights(
                    closedControlPoints.length,
                    metadata.weights,
                    closedControlPoints.length !== controlPoints.length,
                ),
                firstParameter: 0,
                lastParameter: 1,
            };
        }

        if (metadata.curveKind === SketchBezierCurveKind.BSpline) {
            const closedControlPoints = this.closeControlPointsIfNeeded(
                controlPoints,
                metadata.closureKind === SketchBezierCurveClosureKind.Closed,
            );
            const degree = Math.max(1, Math.min(
                Math.floor(metadata.degree ?? 3),
                closedControlPoints.length - 1,
            ));
            return {
                kind: 'bspline',
                controlPoints: closedControlPoints,
                weights: this.resolveWeights(
                    closedControlPoints.length,
                    metadata.weights,
                    closedControlPoints.length !== controlPoints.length,
                ),
                degree,
                knots: buildClampedUniformKnots(closedControlPoints.length, degree),
                firstParameter: 0,
                lastParameter: 1,
                periodic: metadata.closureKind === SketchBezierCurveClosureKind.Closed,
            };
        }

        if (metadata.closureKind === SketchBezierCurveClosureKind.Closed) {
            return this.resolveClosedInterpolationDefinition(spline);
        }

        const evaluationDefinition = spline.getCurveEvaluationDefinition();
        if (evaluationDefinition && evaluationDefinition.controlPoints.length >= 2) {
            return {
                kind: 'bspline',
                controlPoints: evaluationDefinition.controlPoints.map((point) => (
                    spline.referencePlane.worldToLocal(point.x, point.y, point.z)
                )),
                weights: this.resolveWeights(
                    evaluationDefinition.controlPoints.length,
                    evaluationDefinition.weights,
                    false,
                ),
                degree: evaluationDefinition.degree,
                knots: [...evaluationDefinition.knots],
                firstParameter: evaluationDefinition.firstParameter,
                lastParameter: evaluationDefinition.lastParameter,
                periodic: evaluationDefinition.periodic,
            };
        }

        // 两个插值点严格退化为有限线段，不需要等待 Worker 再导出等价一次 B-Spline。
        if (controlPoints.length === 2) {
            return {
                kind: 'bezier',
                controlPoints,
                weights: [1, 1],
                firstParameter: 0,
                lastParameter: 1,
            };
        }
        return null;
    }

    /**
     * 将样条原生参数映射为 Trim 归一化参数。
     *
     * @param definition 样条数学定义。
     * @param nativeParameter 原生曲线参数。
     * @param isClosed 草图曲线是否闭合。
     * @returns Trim `[0,1]` 参数；闭合末端接缝规范为 `0`。
     */
    public normalizeParameter(
        definition: LineSplineCurveDefinition2D,
        nativeParameter: number,
        isClosed: boolean,
    ): number {
        const firstParameter = definition.firstParameter ?? 0;
        const lastParameter = definition.lastParameter ?? 1;
        const parameterSpan = lastParameter - firstParameter;
        if (!Number.isFinite(parameterSpan) || parameterSpan <= Number.EPSILON) {
            throw new Error('样条参数区间退化，无法映射 Trim 参数。');
        }
        const normalized = THREE.MathUtils.clamp(
            (nativeParameter - firstParameter) / parameterSpan,
            0,
            1,
        );
        if (
            isClosed
            && normalized >= 1 - SKETCH_GEOMETRY_TOLERANCE_CONFIG.trimIntersectionParameter
        ) {
            return 0;
        }
        return normalized;
    }

    /**
     * 按 Trim 归一化参数计算样条真实世界点。
     *
     * @param spline 普通样条或样式样条。
     * @param normalizedParameter Trim 归一化参数。
     * @returns 真实参数曲线世界点；定义暂不可用时返回 `null`。
     */
    public evaluateWorldPoint(
        spline: SketchSplineObject | SketchStyleSplineObject,
        normalizedParameter: number,
    ): THREE.Vector3 | null {
        const definition = this.resolveDefinition(spline);
        if (!definition) {
            return null;
        }
        const isClosed = spline.getCurveMetadata().closureKind === SketchBezierCurveClosureKind.Closed;
        const normalized = isClosed
            ? ((normalizedParameter % 1) + 1) % 1
            : THREE.MathUtils.clamp(normalizedParameter, 0, 1);
        const firstParameter = definition.firstParameter ?? 0;
        const lastParameter = definition.lastParameter ?? 1;
        const nativeParameter = THREE.MathUtils.lerp(firstParameter, lastParameter, normalized);
        const localPoint = definition.kind === 'bezier'
            ? this.bezierEvaluator.evaluatePoint(
                definition.controlPoints,
                this.resolveWeights(definition.controlPoints.length, definition.weights ?? [], false),
                (nativeParameter - firstParameter) / (lastParameter - firstParameter),
            )
            : this.bsplineEvaluator.evaluatePoint(definition, nativeParameter);
        return spline.referencePlane.localToWorld(localPoint);
    }

    /**
     * 创建闭合插值样条的周期三次 B-Spline 定义。
     *
     * @param spline 闭合普通样条或闭合样式样条。
     * @returns 与显示和 Profile 共用周期插值公式等价的 B-Spline 定义。
     */
    private resolveClosedInterpolationDefinition(
        spline: SketchSplineObject | SketchStyleSplineObject,
    ): RationalBSplineCurveDefinition2D | null {
        const interpolationPoints = resolveUniqueClosedSplineInterpolationPoints(
            spline.getControlPoints().map((point) => point.getPosition()),
        );
        if (interpolationPoints.length < 3) {
            return null;
        }
        const periodicControlPoints = resolvePeriodicSplineControlPoints(interpolationPoints)
            .map((point) => spline.referencePlane.worldToLocal(point.x, point.y, point.z));
        const controlPointCount = periodicControlPoints.length;
        const expandedControlPoints = [
            periodicControlPoints[controlPointCount - 1]!.clone(),
            ...periodicControlPoints.map((point) => point.clone()),
            periodicControlPoints[0]!.clone(),
            periodicControlPoints[1]!.clone(),
        ];
        const degree = 3;
        const knots = Array.from(
            {length: expandedControlPoints.length + degree + 1},
            (_unused, index) => index,
        );
        return {
            kind: 'bspline',
            controlPoints: expandedControlPoints,
            weights: expandedControlPoints.map(() => 1),
            degree,
            knots,
            firstParameter: degree,
            lastParameter: degree + controlPointCount,
            periodic: true,
        };
    }

    /**
     * 闭合曲线缺少显式尾部重复点时补回首点。
     *
     * @param controlPoints 原始控制点。
     * @param isClosed 是否要求闭合。
     * @returns 独立控制点列表。
     */
    private closeControlPointsIfNeeded(
        controlPoints: readonly THREE.Vector2[],
        isClosed: boolean,
    ): THREE.Vector2[] {
        const result = controlPoints.map((point) => point.clone());
        if (!isClosed || result.length < 2) {
            return result;
        }
        const firstPoint = result[0]!;
        const lastPoint = result[result.length - 1]!;
        if (firstPoint.distanceToSquared(lastPoint) > Number.EPSILON) {
            result.push(firstPoint.clone());
        }
        return result;
    }

    /**
     * 将权重补齐到控制点数量，并在闭合补点时同步复制首权重。
     *
     * @param pointCount 最终控制点数量。
     * @param sourceWeights 原始权重。
     * @param appendedClosurePoint 是否追加了闭合首点。
     * @returns 与控制点一一对应的正权重。
     */
    private resolveWeights(
        pointCount: number,
        sourceWeights: readonly number[],
        appendedClosurePoint: boolean,
    ): number[] {
        return Array.from({length: pointCount}, (_unused, index) => {
            const sourceIndex = appendedClosurePoint && index === pointCount - 1 ? 0 : index;
            const weight = sourceWeights[sourceIndex];
            return Number.isFinite(weight)
                ? Math.max(SKETCH_GEOMETRY_TOLERANCE_CONFIG.splineMinimumWeight, weight!)
                : 1;
        });
    }
}
